<template>
	<BasePlaceHolder :placehold="loading">
		<div class="article-chart">
			<header class="article-chart__header">
				<div v-if="hasTitle" class="line-height-125 body-lg font-weight-bold heading-text-color">
					{{ title }}
				</div>
				<div v-if="hasSubtitle">
					{{ subtitle }}
				</div>
				<div
					v-if="showLegend && chartOptions"
					:class="{ 'align-items-center justify-content-center': isCentered }"
					class="display-flex body-sm line-height-125 mt-50"
				>
					<div
						v-for="(seriesItem, index) in chartOptions.series"
						:key="index"
						:class="{ 'ml-50': index > 0 }"
					>
						<span class="dot mr-50" :style="`'background-color': ${seriesItem.color}`" />
						<span>{{ seriesItem.name }}</span>
					</div>
				</div>
			</header>

			<div v-if="chartOptions" class="article-chart__plotbox-placeholder mt-50">
				<highcharts :options="chartOptions" class="article-chart__plotbox pointer-events-none"></highcharts>
			</div>

			<footer v-if="showFooter" :class="{ 'text-align-center': isCentered }" class="article-chart__source mt-50">
				<div class="body-xs muted line-height-125"> Source: {{ source }} </div>
			</footer>
		</div>
	</BasePlaceHolder>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { DataVisualizationDetails, SeriesElementOptions } from 'types/charts';
import { primaryDefaultChartColor, secondaryDefaultChartColor } from '@constants/highcharts-vue';
import DataVisualizationBaseChart from '@components/charts/DataVisualizationBaseChart';
import deepMerge from 'deepmerge';
import { getDataVisualization } from '@api/data-visualization';
import { Chart as highcharts } from 'highcharts-vue';
import { Options } from 'highcharts';
import { useRoute } from 'vue-router';

const route = useRoute();

const dataVisualizationName = ref('');
const dataVisualizationDetails = ref<DataVisualizationDetails | null>(null);

const showFooter = ref(false);
const showLegend = ref(false);
const hasTitle = ref(false);
const hasSubtitle = ref(false);
const title = ref('');
const subtitle = ref('');
const source = ref('');
const loading = ref(true);
const chartOptions = ref<Options | null>(null);

const isCentered = computed((): boolean => dataVisualizationDetails.value?.layout === 'center');

function processChartOptions() {
	if (dataVisualizationDetails.value?.highchartsObject) {
		const options = dataVisualizationDetails.value.highchartsObject;
		const series: Array<SeriesElementOptions> = options.series as Array<SeriesElementOptions>;
		series.forEach((item) => {
			if (item.data && item.dataFormat === 'datetime') {
				item.data.forEach((datum) => (datum.x = new Date(datum.x)));
			}
		});
		if (!!series[0] && !series[0].color) {
			series[0].color = primaryDefaultChartColor;
		}
		if (series.length === 2 && !series[1].color) {
			series[1].color = secondaryDefaultChartColor;
		}
		chartOptions.value = deepMerge(new DataVisualizationBaseChart().getOptions(), options);
	} else {
		chartOptions.value = null;
	}
}

async function setup(): Promise<void> {
	if (route.params.dataVisualization) {
		dataVisualizationName.value = route.params.dataVisualization as string;
		dataVisualizationDetails.value = await getDataVisualization(dataVisualizationName.value);
		showFooter.value = !!dataVisualizationDetails.value.source;
		showLegend.value = dataVisualizationDetails.value.showLegend ?? false;
		title.value = dataVisualizationDetails.value.title ?? '';
		subtitle.value = dataVisualizationDetails.value.subtitle ?? '';
		hasTitle.value = !!dataVisualizationDetails.value.title;
		hasSubtitle.value = !!dataVisualizationDetails.value.subtitle;
		source.value = dataVisualizationDetails.value.source ?? '';
		processChartOptions();
		loading.value = false;
	}
}

setup();
</script>
